import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../hooks/useAuth';
import { LogsTable } from '../components/LogsTable';
import { Search, RefreshCw, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface LogEntry {
  eventId: string;
  eventTime: string;
  level: string;
  message: string;
  service?: string;
  environment?: string;
  traceId?: string;
  attributes?: Record<string, unknown>;
}

interface QueryResponse {
  logs: LogEntry[];
  total: number;
  hasMore: boolean;
  cursor?: string;
  bytesScanned: number;
}

interface Project {
  id: string;
  name: string;
  apiKey: string;
}

function LogsPage() {
  const { accessToken } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [timeRange, setTimeRange] = useState('1h');
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { data: projectsData } = useQuery<{ projects: Project[] }>({
    queryKey: ['projects'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const response = await fetch(`${API_URL}/v1/user/projects`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    },
    enabled: !!accessToken,
  });

  const projects = projectsData?.projects || [];

  useEffect(() => {
    if (projects.length > 0 && !projectId) {
      setProjectId(projects[0].id);
    }
  }, [projects, projectId]);

  const getTimeRange = () => {
    const to = new Date();
    const from = new Date();
    switch (timeRange) {
      case '15m':
        from.setMinutes(from.getMinutes() - 15);
        break;
      case '1h':
        from.setHours(from.getHours() - 1);
        break;
      case '6h':
        from.setHours(from.getHours() - 6);
        break;
      case '24h':
        from.setHours(from.getHours() - 24);
        break;
      default:
        from.setHours(from.getHours() - 1);
    }
    return { from: from.toISOString(), to: to.toISOString() };
  };

  const selectedProject = projects.find((p) => p.id === projectId);

  const { data, isLoading, error, refetch, isFetching } = useQuery<QueryResponse>({
    queryKey: ['logs', projectId, timeRange, levelFilter, debouncedSearchQuery],
    queryFn: async () => {
      if (!projectId || !selectedProject) throw new Error('Project ID required');
      const { from, to } = getTimeRange();
      const response = await fetch(`${API_URL}/v1/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${selectedProject.apiKey}`,
        },
        body: JSON.stringify({
          projectId,
          from,
          to,
          level: levelFilter.length > 0 ? levelFilter : undefined,
          query: debouncedSearchQuery || undefined,
          limit: 100,
        }),
      });
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Query failed: ${response.status} - ${errorBody}`);
      }
      return response.json();
    },
    enabled: !!projectId && !!selectedProject,
    refetchInterval: 30000,
  });

  const toggleLevel = (level: string) => {
    setLevelFilter((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const levelColors: Record<string, string> = {
    debug: 'bg-gray-500/15 text-gray-400 hover:bg-gray-500/25',
    info: 'bg-blue-500/15 text-blue-400 hover:bg-blue-500/25',
    warn: 'bg-yellow-500/15 text-yellow-400 hover:bg-yellow-500/25',
    error: 'bg-red-500/15 text-red-400 hover:bg-red-500/25',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Log Viewer</h1>
        <p className="text-muted-foreground">Search and analyze your application logs</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15m">Last 15 minutes</SelectItem>
                <SelectItem value="1h">Last 1 hour</SelectItem>
                <SelectItem value="6h">Last 6 hours</SelectItem>
                <SelectItem value="24h">Last 24 hours</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Button onClick={() => refetch()} disabled={!projectId || isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Loading...' : 'Refresh'}
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Filter by level:</span>
            {(['debug', 'info', 'warn', 'error'] as const).map((level) => (
              <button
                key={level}
                onClick={() => toggleLevel(level)}
                className={`rounded-md border px-3 py-1 text-xs font-medium transition-colors ${
                  levelFilter.includes(level)
                    ? levelColors[level] + ' border-current'
                    : 'border-border bg-muted text-muted-foreground hover:bg-accent'
                }`}
              >
                {level.toUpperCase()}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {!projectId && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-foreground">
              {projects.length === 0 ? 'No projects yet' : 'Select a project'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {projects.length === 0
                ? 'Create a project first to start viewing logs'
                : 'Choose a project to view its logs'}
            </p>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">Error: {(error as Error).message}</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Found <span className="font-medium text-foreground">{data.total}</span> logs
            </span>
            <span>
              <span className="font-medium text-foreground">
                {(data.bytesScanned / 1024).toFixed(1)} KB
              </span>{' '}
              scanned
            </span>
            {data.hasMore && <Badge variant="secondary">More available</Badge>}
          </div>

          <LogsTable logs={data.logs} height={500} />
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/logs')({
  component: LogsPage,
});
